import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '../../components/feature/SiteHeader';
import SiteFooter from '../../components/feature/SiteFooter';
import ArticleCard from '../../components/feature/ArticleCard';
import Breadcrumb from '../../components/feature/Breadcrumb';
import SectionHeading from '../../components/feature/SectionHeading';
import { useFavorites } from '../../hooks/useFavorites';
import {
  formatViews,
  getArticleById,
  getCategoryByKey,
  getRelatedArticles,
} from '../../lib/content';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center py-20">
            <i className="ri-file-forbid-line text-5xl text-foreground-300" />
            <h1 className="mt-5 font-heading font-bold text-xl text-foreground-900">
              该内容已下架或不存在
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              你访问的内容可能已被移除，去首页看看其他精彩内容吧
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line" />
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = getCategoryByKey(article.category);
  const related = getRelatedArticles(article, 3);
  const favorited = isFavorite(article.id);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(''), 2200);
  };

  const handleCopy = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('链接已复制（演示）');
    }
    setShareOpen(false);
  };

  const handleFavorite = () => {
    const next = toggleFavorite(article.id);
    showToast(next ? '已加入本地收藏（演示）' : '已取消收藏');
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full px-4 md:px-8 py-6">
        <div className="max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              category
                ? { label: category.name, to: `/category/${category.key}` }
                : { label: '内容' },
              { label: article.title },
            ]}
          />

          <article className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              {category && (
                <Link
                  to={`/category/${category.key}`}
                  className="text-xs font-medium text-accent-700 bg-accent-50 px-2.5 py-1 rounded-md hover:bg-accent-100 transition-colors"
                >
                  {category.name}
                </Link>
              )}
            </div>

            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-snug">
              {article.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500 pb-5 border-b border-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line" />
                {formatViews(article.views)} 阅读
              </span>
              <div className="flex items-center gap-1">
                {article.tags.map((t) => (
                  <span
                    key={t}
                    className="text-xs text-secondary-900 bg-secondary-100 px-2 py-0.5 rounded-md"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-lg overflow-hidden">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - WWW.17C.COM`}
                className="w-full h-auto object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((para, index) => (
                <div key={index}>
                  <p className="text-base md:text-lg text-foreground-800 leading-[1.9]">
                    {para}
                  </p>
                  {index === 1 && category && (
                    <figure className="mt-6">
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src={category.cover}
                          alt={`${category.name}相关配图`}
                          title={`${category.name} - WWW.17C.COM`}
                          className="w-full h-auto object-cover object-top"
                        />
                      </div>
                      <figcaption className="mt-2 text-center text-xs text-foreground-500">
                        配图与文章主题相关（演示素材）
                      </figcaption>
                    </figure>
                  )}
                </div>
              ))}
            </div>

            {/* 分享与收藏 */}
            <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享到</span>
                <button
                  type="button"
                  onClick={() => setShareOpen((v) => !v)}
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  aria-label="分享"
                >
                  <i className="ri-share-line" />
                </button>
                <a
                  href="https://bjparadise.cn/share/share.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-600 transition-colors"
                  aria-label="分享到微博"
                >
                  <i className="ri-weibo-line" />
                </a>
                <button
                  type="button"
                  onClick={() => showToast('请打开微信扫描二维码分享（演示）')}
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer"
                  aria-label="分享到微信"
                >
                  <i className="ri-wechat-line" />
                </button>
              </div>

              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? 'bg-accent-500 text-white hover:bg-accent-600'
                    : 'border border-primary-300 text-primary-600 hover:bg-primary-50'
                }`}
              >
                <i className={favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                {favorited ? '已收藏' : '收藏'}
              </button>
            </div>

            {shareOpen && (
              <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-4 animate-fade-in-up">
                <p className="text-sm text-foreground-600 mb-3">选择分享方式：</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-link" />
                    复制链接
                  </button>
                  <a
                    href="https://bjparadise.cn/share/share.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-accent-300 hover:text-accent-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-weibo-line" />
                    微博
                  </a>
                  <button
                    type="button"
                    onClick={() => showToast('请打开微信扫描二维码分享（演示）')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-secondary-300 hover:text-secondary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-wechat-line" />
                    微信
                  </button>
                </div>
              </div>
            )}
          </article>

          {/* 相关推荐 */}
          <section className="mt-14">
            <SectionHeading title="相关推荐" icon="ri-stack-line" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} variant="grid" />
              ))}
            </div>
          </section>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm animate-fade-in-up">
          {toast}
        </div>
      )}

      <SiteFooter />
    </div>
  );
}